/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { flatMapDeep } from 'src/core/utils/array';
import { createConversationFeatureStore } from 'src/luis/core/stores/conversationFeatureStore';
import { PhraseList } from 'src/luis/modules/features/types/PhraseList';
import { ModelType } from 'src/luis/types/ModelType';
import { CompositeEntityStore } from 'src/luis/types/stores/entities/CompositeEntityStore';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';
import { IntentStore } from 'src/luis/types/stores/intents/IntentStore';

/**
 * Updating the models feature stores when the phrase list is made global .
 *
 * @param param0.phraseList The updated phrase list
 * @param param0.entityStores All the available entity stores in the application
 * @param param0.intentStores All the available intent stores in the application
 * @param param0.isDisabled Indicates whether the phrase list is disabled or not
 */
export const makePhraseListGlobalUpdates = ({
    entityStores,
    intentStores,
    phraseList
}: {
    entityStores: EntityStore[];
    intentStores: IntentStore[];
    phraseList: PhraseList;
}) => {
    const entityStoresWithMlChildren = flatMapDeep(entityStores, (e: SimpleEntityStore) => e.children || []);
    entityStoresWithMlChildren.filter(doesEntityStoreSupportFeatures).forEach(e => {
        if (!e.featureStoreHouse.findById(phraseList.id)) {
            e.featureStoreHouse.add(createConversationFeatureStore(phraseList.id, { typeId: 'feature' }));
        }
    });
    intentStores.forEach(i => {
        if (!i.featureStoreHouse.findById(phraseList.id)) {
            i.featureStoreHouse.add(createConversationFeatureStore(phraseList.id, { typeId: 'feature' }));
        }
    });
};

const doesEntityStoreSupportFeatures = (entity: EntityStore): entity is SimpleEntityStore | CompositeEntityStore =>
    entity.typeId === ModelType.Simple || entity.typeId === ModelType.Composite;
